import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import mergeRefs from "merge-refs";
import classNames from "classnames";
import SearchIcon from "@/assets/svgs/search.svg";
import { CloseIcon } from "@/assets/svgs/icons";


export interface SearchBarRef extends HTMLInputElement {
    clear: () => void;
};

interface SearchBarProps {
    width?: number | string;
    className?: string;
    defaultValue?: string;
    placeholder?: string;
    formAriaLabel?: string;
    clearButtonAriaLabel?: string;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch?: (query: string) => void | Promise<void>;
}

const SearchBar = forwardRef<SearchBarRef, SearchBarProps>((props, ref) => {
    const {
        width,
        className: propsClassName,
        defaultValue,
        placeholder,
        formAriaLabel,
        clearButtonAriaLabel,
        onKeyDown,
        onInput,
        onSearch
    } = props;

    const [searched, setSearched] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const search = (value: string) => {
        if (onSearch) {
            onSearch(value);
        }
        setSearched(!!value);
    };

    const onClickClear = () => {
        if (!inputRef.current) {
            return;
        }
        inputRef.current.value = "";
        setSearched(false);
        inputRef.current.focus();
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        search(inputRef.current?.value ?? "");
    };

    useImperativeHandle(ref, () => ({
        clear: () => {
            if (!inputRef.current) {
                return;
            }
            inputRef.current.value = "";
            setSearched(false);
            onSearch?.("");
        },
        ...inputRef.current as HTMLInputElement
    }));

    return (
        <form className="relative"
              style={{ width: width ?? "100%" }}
              onSubmit={onSubmit}
              role="search"
              aria-label={formAriaLabel}
        >
            <input type="search"
                   ref={mergeRefs(inputRef, ref)}
                   defaultValue={defaultValue ?? ""}
                   className={classNames(
                       "input-search pl-3.5",
                       searched && "searched",
                       propsClassName
                   )}
                   placeholder={placeholder}
                   onKeyDown={onKeyDown}
                   onInput={onInput}
            />
            <span className="absolute search-icon right-2.5 top-3">
                <img src={SearchIcon} alt="" className="size-4" />
            </span>
            <button type="button"
                    className="absolute input-search-cancel-button right-2.5 top-3"
                    onClick={onClickClear}
                    aria-label={clearButtonAriaLabel}
            >
                <CloseIcon className="size-4" />
            </button>
        </form>
    );
});

export default SearchBar;
